<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop,
            'q-mb-lg': $q.platform.is.desktop
        }"
    >
        <q-form @submit="handleSubmit" ref="eventForm">
            <banner
                class="q-mb-md"
                :is-visible="getUneditableErrorMessage(end) != null"
                type="info"
                :message="getUneditableErrorMessage(end)"
            />
            <!-- status dropdown -->
            <q-select
                v-model="form.status"
                :label="$t('Status')"
                :options="statusOptions"
                class="q-mb-md full-width"
                outlined
                lazy-rules
                :disable="true"
                bg-color="white"
                color="accent"
            >
                <!-- Option style -->
                <template v-slot:option="scope">
                    <q-item v-bind="scope.itemProps" v-on="scope.itemEvents">
                        <q-item-section class="row">
                            <div>
                                <q-icon
                                    :color="
                                        ASSET_STATUS_TO_STYLE[scope.opt.value][
                                            'color'
                                        ]
                                    "
                                    name="fas fa-circle"
                                    class="q-mr-sm"
                                ></q-icon>
                                {{ $t(scope.opt.label) }}
                            </div>
                        </q-item-section>
                    </q-item>
                </template>
                <!-- Selected item style -->
                <template v-slot:selected>
                    <q-icon
                        :color="form.status['color']"
                        style="margin-bottom: 2px"
                        name="fas fa-circle"
                    ></q-icon>
                    <div style="margin-left: 5px">
                        {{ $t(form.status['label']) }}
                    </div>
                </template>
            </q-select>
            <!-- asset dropdown -->
            <q-select
                v-model="form.asset"
                :options="assetOptions"
                :label="$t('Asset')"
                :rules="[
                    val => (val !== '' && val !== null) || $t('Select an asset')
                ]"
                class="q-mb-lg"
                behavior="dialog"
                clearable
                outlined
                emit-value
                map-options
                disable
                bg-color="white"
                color="accent"
            />
            <!-- Product select -->
            <table-select
                v-model="form.product"
                @confirmSelection="form.product = productSelectProps.selection"
                :baseURL="productSelectProps.url"
                :queryParams="productSelectProps.queryParams"
                :label="productSelectProps.label"
                :dialogTitle="productSelectProps.dialogTitle"
                :searchLabel="productSelectProps.searchLabel"
                :disable="isPhaseInProgress(end) || !isPhaseEditable(end)"
                row-key="id"
                class="q-mb-lg"
            >
                <template v-slot:item="data">
                    <product-card
                        @select="
                            productSelectProps.selection = {
                                label: data.props.row.name,
                                value: data.props.row.product
                            }
                        "
                        :name="data.props.row.name"
                        :code="data.props.row.code"
                        :description="data.props.row.description"
                        :unit-measure="data.props.row.unit_measure"
                        :unit-packing="data.props.row.unit_packing"
                        :isSelected="
                            productSelectProps.selection &&
                            productSelectProps.selection.value ===
                                data.props.row.product
                        "
                        :isSelectable="true"
                    ></product-card>
                </template>
            </table-select>

            <!-- Cause select -->
            <table-select
                id="cause-selector"
                v-model="form.cause"
                @confirmSelection="form.cause = causeSelectProps.selection"
                :baseURL="causeSelectProps.url"
                :queryParams="causeSelectProps.queryParams"
                :label="causeSelectProps.label"
                :dialogTitle="causeSelectProps.dialogTitle"
                :searchLabel="causeSelectProps.searchLabel"
                row-key="id"
                class="q-mb-lg"
                :disable="!isPhaseEditable(end)"
            >
                <template v-slot:item="data">
                    <cause-card
                        @select="
                            causeSelectProps.selection = {
                                label: data.props.row.name,
                                value: data.props.row.id
                            }
                        "
                        :name="data.props.row.name"
                        :lossType="data.props.row.type_of_loss"
                        :categoryName="data.props.row.category_name"
                        :planned="data.props.row.planned"
                        :description="data.props.row.description"
                        :targetDowntime="data.props.row.target_down_time"
                        :isSelected="
                            causeSelectProps.selection &&
                            causeSelectProps.selection.value ===
                                data.props.row.id
                        "
                        :isSelectable="true"
                        :equipmentFailure="data.props.row.equipment_failure"
                    ></cause-card>
                </template>
            </table-select>

            <!-- start datetime picker -->
            <div>
                <date-time-picker
                    v-model="form.start"
                    :disable="true"
                    :label="$t('Start')"
                    :rules="[val => val !== '' || $t('Pick a date and time')]"
                    :error-message="dateRangeErrorMessage"
                ></date-time-picker>
                <q-tooltip>
                    {{
                        $t(
                            'Go to the asset timeline page to edit the time of this event.'
                        )
                    }}
                </q-tooltip>
            </div>
            <!-- end datetime picker -->
            <div>
                <date-time-picker
                    v-model="form.end"
                    :disable="true"
                    :label="$t('End')"
                    :error-message="dateRangeErrorMessage"
                ></date-time-picker>
                <q-tooltip>
                    {{
                        $t(
                            'Go to the asset timeline page to edit the time of this event.'
                        )
                    }}
                </q-tooltip>
            </div>
            <!-- Proximity Sensor options dropdown -->
            <q-select
                v-if="proximityReading !== null"
                v-model="form.proximityReading"
                :options="proximitySensorOptions"
                :label="$t('Product on line?')"
                :rules="[
                    val =>
                        (val !== '' && val !== null) || $t('Select an Option')
                ]"
                class="q-mb-lg"
                behavior="dialog"
                outlined
                emit-value
                map-options
                bg-color="white"
                color="accent"
            />
            <!-- description input -->
            <q-input
                v-model="form.description"
                @clear="form.description = ''"
                data-test="description-textarea"
                :label="$t('Description')"
                :input-style="'min-height: 45px;'"
                style="width: 100%"
                class="q-mb-lg"
                type="textarea"
                clearable
                outlined
                autogrow
                bg-color="white"
                color="accent"
                :disable="!isPhaseEditable(end)"
            />

            <!-- action buttons -->
            <div class="form__actions">
                <q-btn
                    v-show="form.asset"
                    @click="
                        $router.push({
                            name: 'asset_timeline',
                            params: {
                                assetId: form.asset,
                                facilityId: facilityId
                            }
                        })
                    "
                    :label="$t('See asset timeline')"
                    icon="fas fa-history"
                    class="q-mb-md q-px-sm bg-white"
                    color="primary"
                    no-caps
                    outline
                >
                </q-btn>

                <!-- 'Save' button -->
                <q-btn
                    :label="$t('Save')"
                    :disable="$userCannot('edit', 'phases') || !formHasChanged"
                    class="float-right"
                    :loading="isLoading"
                    type="submit"
                    color="primary"
                    id="save-button"
                />
                <!-- 'Cancel' button -->
                <q-btn
                    @click="closeDetail"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm bg-white"
                    color="white"
                    text-color="primary"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import CauseCard from '@components/CauseCard.vue';
import ProductCard from '@components/ProductCard.vue';
import TableSelect from '@components/TableSelect.vue';
import { PROXIMITY_SENSOR_OPTIONS } from '@models/devices.js';
import { ASSET_STATUS_TO_STYLE, STATUS_OPTIONS } from '@models/assets.js';
import {
    isPhaseEditable,
    isPhaseInProgress,
    getUneditableErrorMessage
} from '@utils/phaseChecker.js';
import Banner from '@components/Banner.vue';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useNotificationsStore } from '@stores/notifications.ts';

export default {
    name: 'EventsDetail',
    components: { Banner, CauseCard, ProductCard, DateTimePicker, TableSelect },
    mixins: [BreadcrumbsManager],
    emits: ['save', 'return'],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        },
        eventId: {
            type: [Number, String],
            required: false,
            default: null
        },
        eventUUID: {
            type: String,
            required: true
        },
        product: {
            type: [Number, String],
            required: false,
            default: null
        },
        cause: {
            type: [Number, String],
            required: false,
            default: null
        },
        asset: {
            type: [Number, String],
            required: false,
            default: null
        },
        status: {
            type: String,
            required: false
        },
        start: {
            type: String,
            required: false,
            default: ''
        },
        end: {
            type: [String, null],
            required: false,
            default: ''
        },
        description: {
            type: String,
            required: false,
            default: ''
        },
        proximityReading: {
            type: [Number, String],
            required: false,
            default: null
        },
        causeName: {
            type: String,
            required: false,
            default: null
        },
        productName: {
            type: String,
            required: false,
            default: null
        },
        editEvent: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    async created() {
        // begin loading
        this.beginLoading(`${this.$t('Loading event')}...`);
        // init form on update mode
        if (this.isEdit) {
            await this.initForm();
        } else {
            // init time range in the datetime picker in create mode
            this.initDateTimeRange();
        }

        // init the options for asset dropdown
        this.initAssetOptions();

        // stop loading
        this.stopLoading();
    },
    data() {
        return {
            form: {
                asset: '',
                cause: '',
                product: '',
                status: '',
                start: '',
                end: '',
                description: '',
                proximityReading: null
            },
            isLoading: false,
            statusOptions: STATUS_OPTIONS,
            dateRangeErrorMessage: '',
            assetOptions: [],
            disableProduct: true,
            disableCause: true,

            // remote selectors needs to be remounted when asset changes, as the cause/product values
            // are reset to null, causing the form validators to raise errors
            remoteSelectKey: 0,

            // selectors
            causeSelectProps: {
                url: `assets/${this.asset}/causes/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            },
            productSelectProps: {
                url: `assets/${this.asset}/products/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            proximitySensorOptions: PROXIMITY_SENSOR_OPTIONS
        };
    },
    computed: {
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.$store.getters['facilities/getFacility'](
                this.facilityId
            );
        },
        /**
         * Determine if form is in edit mode.
         * @return {boolean}
         */
        isEdit() {
            return this.eventId !== null;
        },
        /**
         * Server formatted payload.
         * @return {Object}
         */
        payload() {
            const payload = {
                facility: parseInt(this.facilityId),
                asset: parseInt(this.form['asset']),
                start: this.form['start'],
                end:
                    this.form['end'] === '' || this.form['end'] === null
                        ? null
                        : this.form['end'],
                description: this.form['description'],
                proximity_reading:
                    this.form.proximityReading === 'unknown'
                        ? null
                        : this.form.proximityReading
            };

            if (this.form.cause) {
                payload['cause'] = parseInt(this.form.cause.value);
            }

            if (this.form.product) {
                payload['product'] = parseInt(this.form.product.value);
            }

            return payload;
        },
        productUrl() {
            if (!this.form.asset) {
                return '';
            }
            let productUrl = `/assets/${this.form.asset}/products/`;
            return productUrl;
        },
        causeUrl() {
            if (!this.form.asset) {
                return '';
            }
            let causeUrl = `/assets/${this.form.asset}/causes/`;
            return causeUrl;
        },
        /**
         * Enable the submit button when the user updates the form.
         * @return {Boolean}
         */
        formHasChanged() {
            return (
                this.dateToIsoString(this.start) !=
                    this.dateToIsoString(this.form.start) ||
                this.dateToIsoString(this.end) !=
                    this.dateToIsoString(this.form?.end) ||
                this.asset != this.form.asset ||
                this.cause != this.form.cause?.value ||
                this.product != this.form.product?.value ||
                this.description != this.form.description
            );
        }
    },
    watch: {
        'form.asset': {
            immediate: true,
            handler(currId, prevId) {
                // clear the dropdown if the user selects a different asset
                if (prevId && currId !== prevId) {
                    this.form.product = null;
                    this.form.cause = null;
                    // remount remote selectors
                    this.remoteSelectKey++;
                }

                // corner case: the asset id is null or empty
                if (!currId) {
                    // disable the product and cause dropdowns
                    this.disableProduct = true;
                    this.disableCause = true;
                } else {
                    // when the asset id exists enable the dropdowns
                    this.disableProduct = false;
                    this.disableCause = false;
                }
            }
        }
    },
    methods: {
        isPhaseEditable,
        isPhaseInProgress,
        getUneditableErrorMessage,
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useNotificationsStore, ['resolveNotificationAction']),
        /**
         * Init event detail form.
         */
        async initForm() {
            // check whether the only parameters in the router view is 'facilityId' and 'eventId', since if this is not the
            // case then the user is not entering the router view from the Downtime report list view, so we need to fetch event details.
            const isUserFromListView =
                Object.keys(this.$route.params).length !== 2;

            let event = {
                eventId: null,
                product: null,
                cause: null,
                asset: null,
                start: null,
                end: null,
                description: null,
                proximityReading: null,
                causeName: null,
                productName: null
            };

            if (this.editEvent) {
                // CHeck if user is accessing the Event Detail page from Event List Timeline view

                event.asset = this.asset;
                event.start = this.start;
                event.end = this.end;
                event.description = this.description;
                event.proximityReading = this.proximityReading;
                event.cause = this.cause;
                event.causeName = this.causeName;

                event.product = this.product;
                event.productName = this.productName;
            } else if (isUserFromListView) {
                // get event detail from props passed from the list view
                event = this.$route.params;
            } else {
                // get event detail from the server
                event = await this.getEvent();
                // rename fields
                event['causeName'] = event.cause_name;
                event['productName'] = event.product_name;
            }
            // initialize form
            this.form['asset'] = event.asset;
            this.form['start'] = event.start;
            this.form['end'] = event.end;
            this.form['description'] = event.description;
            const proximity_reading =
                event.proximityReading === null
                    ? 'unknown'
                    : event.proximityReading;
            this.form.proximityReading = proximity_reading;
            this.form['cause'] = {
                value: event.cause,
                label: event.causeName
            };
            this.form['product'] = {
                value: event.product,
                label: event.productName
            };

            // set status option
            this.form['status'] = this.statusOptions.filter(
                option => option.value === this.status
            )[0];
        },
        /**
         * Init the options of the asset dropdown
         */
        initAssetOptions() {
            // get the assets from store
            const assets = this.$store.getters['facilities/getAssets'](
                this.facilityId
            );
            // init the asset options
            assets.forEach(asset => {
                this.assetOptions.push({
                    label: asset['name'],
                    value: asset['id']
                });
            });
        },
        /**
         * Validate product field.
         * @param {String} product
         * @return {Boolean} - Whether the field value is valid or not.
         */
        validateProduct(product) {
            // check whether product field needs to be validated
            if (this.form.status.value !== 'stopped') {
                return Boolean(product);
            } else {
                return true;
            }
        },
        /**
         * Handle the behavior when submitting the form
         */
        async handleSubmit() {
            // validate datetime range
            let isValid = true;
            // change the format of datetime to server format
            const start = this.form.start;
            const end = this.form.end;
            if (end && start >= end) {
                this.dateRangeErrorMessage = this.$t(
                    'Start must be before end'
                );
                isValid = false;
            }

            // validate product field
            if (!this.validateProduct(this.form.product?.value)) {
                this.$notifyError(
                    this.$t('Please select a product to update this event.')
                );
                isValid = false;
            }

            if (isValid) {
                this.isLoading = true;
                this.dateRangeErrorMessage = '';
                if (this.isEdit) {
                    await this.update();
                    if (this.editEvent) {
                        this.$emit('save');
                        this.closeDetail();
                    }
                } else {
                    await this.create();
                }
                this.isLoading = false;
            }
        },
        /**
         * Create event.
         */
        async create() {
            await this.$axios
                .post('events/', this.payload)
                .then(response => {
                    this.$notifySuccess(this.$t('Event created'));

                    this.$router.go(-1);
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to create event. Please try again later.'
                        ),
                        error
                    );
                });
        },
        /**
         * Update event.
         */
        async update() {
            const errorMessage = this.$t(
                'Failed to update event. Please try again later.'
            );
            // send PUT http request
            try {
                await this.$axios.put(`events/${this.eventId}/`, this.payload);
                this.$notifySuccess(this.$t('Event updated'));

                if (this.payload.cause) {
                    this.resolveNotificationAction(this.eventUUID);
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if ('uneditable_phase' in error) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(errorMessage, error);
                }
            }
        },
        /**
         * Get event details from the server.
         */
        async getEvent() {
            let event = null;
            await this.$axios
                .get(`events/${this.eventId}/`)
                .then(response => {
                    event = response.data;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t('Failed to fetch event details.'),
                        error
                    );
                });
            return event;
        },
        closeDetail() {
            if (this.editEvent) {
                this.$emit('return');
            } else {
                this.$router.go(-1);
            }
        },

        /**************
         * Datetime related methods *
         *************/

        /**
         * init the time range in ISO 8601 format
         * i.e. start: '2020-03-03T20:35:39.700Z'; end: ''
         */
        initDateTimeRange() {
            // init start time at now
            this.form['start'] = new Date().toISOString();
            // default end time is empty
            this.form['end'] = '';
        },
        /**
         * Convert a given date to ISO String and reset the milliseconds
         * @return {Date}
         */
        dateToIsoString(date) {
            const newDate = new Date(date);
            newDate.setMilliseconds(0);
            return newDate.toISOString();
        }
    }
};
</script>

<style lang="scss" scoped></style>
